import {
  StyledDialog,
  StyledDialogTitle,
} from "../../../components/StyledComponents/Dialog/Dialog.js"; // Import the StyledDialog and StyledDialogTitle components from the Dialog.js file in the StyledComponents/Dialog folder

import React, { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Autocomplete,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Portal,
} from "@mui/material";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  BodyMedium,
  H8TitleMedium,
  H6HeadlineSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import { useForm, Controller } from "react-hook-form";
import PlaceHolderButton from "../../../components/PlaceHolderButton/PlaceHolderButton.js";
import { usePostOrg } from "../../../api/queries/usePostOrg.ts";
import { useAuth0 } from "@auth0/auth0-react";
import {
  StyledCheckbox,
  StyledTextField,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { usePostAlertNotifications } from "../../queries/usePostAlertNotifications.ts";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { useQuery } from "@tanstack/react-query";
import * as api from "../../../api/api.js";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

const NotificationsDialog = ({
  open,
  handleClose,
  refetch: refetchNotifications,
}) => {
  const { colors } = useTheme();
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [severityLevel, setSeverityLevel] = useState("");
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const [org, setOrg] = useState({});
  const [authToken, setAuthToken] = useState("");
  const { isAuthenticated, getAccessTokenSilently } =
    useAuth0(); // Use the useAuth0 hook to get authentication status and token fetching function

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  let membersData = [];

  const {
    data: userData,
    isLoading,
    isFetching,
    isError,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: ["user", open],
    queryFn: ({ queryKey }) =>
      api.GetUserUseQuery(authToken),
    enabled: !!authToken && open,
  });
  const orgId =
    isFetched && userData?.result?.org_id
      ? userData?.result?.org_id
      : "";

  const {
    data: members,
    refetch: membersDataRefetch,
    isLoading: isMembersDataLoading,
    isFetching: isMembersDataFetching,
    isFetched: isMembersDataFetched,
  } = useQuery({
    queryKey: ["membersData", orgId], // Use only serializable values
    queryFn: () => api.GetOrgUseQuery(authToken, orgId), // Pass correct arguments
    enabled: open && !!authToken,
  });

  if (isMembersDataFetched && members) {
    membersData =
      isMembersDataFetched &&
      members?.org?.user &&
      members?.org?.user.length > 0
        ? members?.org?.user.map((user) => user.email)
        : [];
  }

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      selectedEmails: [],
      hashBoardMalfunction: false,
      fanMalfunction: false,
      psuMalfunction: false,
      chipTemperature: false,
      leakDetection: false,
    },
  });

  const selectedEmails = watch("selectedEmails") || [];
  const hashBoardMalfunction = watch(
    "hashBoardMalfunction"
  );
  const fanMalfunction = watch("fanMalfunction");
  const psuMalfunction = watch("psuMalfunction");
  const leakDetection = watch("leakDetection");
  const chipTemperature = watch("chipTemperature");

  const atleastOneSelected =
    hashBoardMalfunction ||
    fanMalfunction ||
    psuMalfunction ||
    leakDetection ||
    chipTemperature;
  const { mutate: subscribeToNotifications } =
    usePostAlertNotifications();
  const onSubmit = (data) => {
    if (!atleastOneSelected) {
      setShowAlert(true);
      setAlertText(
        "Please select at least one event to notify."
      );
      return;
    }
    const finalData = data.selectedEmails.map((email) => {
      const alertNotificationConfig = {
        alertNotificationConfig: { notifications: [] },
      };
      if (data.hashBoardMalfunction) {
        alertNotificationConfig.alertNotificationConfig.notifications.push(
          {
            type: "Hashboard malfunction",
            subscribe: true,
            notifyMe: true,
          }
        );
      }
      if (data.fanMalfunction) {
        alertNotificationConfig.alertNotificationConfig.notifications.push(
          {
            type: "Fan malfunction",
            subscribe: true,
            notifyMe: true,
          }
        );
      }
      if (data.psuMalfunction) {
        alertNotificationConfig.alertNotificationConfig.notifications.push(
          {
            type: "PSU malfunction",
            subscribe: true,
            notifyMe: true,
          }
        );
      }
      if (data.leakDetection) {
        alertNotificationConfig.alertNotificationConfig.notifications.push(
          {
            type: "Leak detection",
            subscribe: true,
            notifyMe: true,
          }
        );
      }
      if (data.chipTemperature) {
        alertNotificationConfig.alertNotificationConfig.notifications.push(
          {
            type: "Chip temperature",
            subscribe: true,
            notifyMe: true,
          }
        );
      }
      return {
        email: email,
        ...alertNotificationConfig,
      };
    });
    subscribeToNotifications(
      {
        ...finalData,
      },
      {
        onSuccess: async (data) => {
          if (data?.status === "ok") {
            setToastMessage(
              "Notification settings saved successfully"
            );
            setToastOpen(true);
            await refetchNotifications();
            setShowAlert(false);
            setAlertText("");
            handleClose();
            reset();
          } else {
            setToastMessage(
              "Failed to save notification settings"
            );
            setToastOpen(true);
            reset();
          }
        },
        onError: (error) => {
          setToastMessage(
            "Failed to save notification settings"
          );
          setToastOpen(true);
        },
      }
    );
    reset();
  };

  const handleChipDelete = (optionToDelete) => {
    const newValue = selectedEmails.filter(
      (option) => option !== optionToDelete
    );

    setValue("selectedEmails", newValue); // Update the state in react-hook-form
  };
  return (
    <StyledDialog
      colors={colors}
      open={open}
      onClose={handleClose}
      hideBackdrop={true}
    >
      <StyledDialogTitle>
        <H6HeadlineSmall style={{ textAlign: "left" }}>
          Add notification
        </H6HeadlineSmall>
        <BodyMedium style={{ textAlign: "left" }}>
          {" "}
          Select the users and what events you want them to
          be notified for.{" "}
        </BodyMedium>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        {showAlert && (
          <Portal>
            <Box
              padding={"0 0.5rem 0.5rem 0.5rem"}
              display={"flex"}
              flexGrow={0}
              sx={{
                position: "fixed",
                top: "30%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2000, // Ensure it appears above the dialog
                width: "auto",
              }}
            >
              <Alert
                severity={"error"}
                onClose={() => setShowAlert(false)}
                sx={{ padding: "0.5rem 0.75rem", color: colors?.primary?.main }}
              >
                {alertText}
              </Alert>
            </Box>
          </Portal>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="row"
            gap={6}
            flexGrow={1}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent={"flex-start"}
              gap={2}
              flexGrow={1}
            >
              <H8TitleMedium>
                Select whom to notify{" "}
              </H8TitleMedium>
              <Controller
                name="selectedEmails"
                control={control}
                defaultValue={null}
                render={({
                  field: { onChange, value, ref },
                }) => (
                  <Autocomplete
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    onChange={(_, data) => onChange(data)}
                    value={value || []}
                    clearIcon={null}
                    sx={{
                      maxWidth: "19rem",
                      minWidth: "19rem",
                    }}
                    ListboxProps={{
                      sx: {
                        maxHeight: "250px",
                        overflowY: "auto",
                        backgroundColor: colors.background.paper,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                                color: colors.text.primary,
                      },
                    }}
                    slotProps={{
                      popupIndicator: {
                        sx: { color: colors.text.muted , 
                        '&:hover': { color: colors.text.primary },
                      }
                      },
                      clearIndicator: {
                        sx: {
                          color: colors.text.muted,
                          '&:hover': { color: colors.text.primary },
                        },
                      },
                    }}
                    options={membersData} // Replace with your options
                    renderInput={(params) => (
                      <StyledTextField colors={colors}
                        {...params}
                        label="Email"
                        variant="outlined"
                        inputRef={ref}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isMembersDataFetching ||
                              isMembersDataLoading ? (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: colors.primary.main,
                                  }}
                                />
                              ) : null}
                              {
                                params.InputProps
                                  .endAdornment
                              }
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={() => null}
                  />
                )}
              />
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                maxHeight={"10.625rem"}
                maxWidth={"20rem"}
                overflow={"auto"}
              >
                {selectedEmails?.map((email, index) => (
                  <PlaceHolderButton
                    key={index}
                    onClose={() => handleChipDelete(email)}
                    text={email}
                  />
                ))}
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent={"flex-start"}
              gap={2}
              flexGrow={1}
            >
              <H8TitleMedium>Select events</H8TitleMedium>
              <Controller
                name="hashBoardMalfunction"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{
                      minHeight: "3.5rem",
                      maxWidth: "20rem",
                      minWidth: "20rem",
                      color: colors.text.primary
                    }}
                    control={<StyledCheckbox {...field} colors={colors} />}
                    label="Hash Board malfunction"
                  />
                )}
              />
              <Controller
                name="fanMalfunction"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{
                      minHeight: "3.5rem",
                      maxWidth: "20rem",
                      minWidth: "20rem",
                      color: colors.text.primary
                    }}
                    control={<StyledCheckbox {...field} colors={colors} />}
                    label="Fan malfunction"
                  />
                )}
              />
              <Controller
                name="psuMalfunction"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{
                      minHeight: "3.5rem",
                      maxWidth: "20rem",
                      minWidth: "20rem",  
                      color: colors.text.primary
                    }}
                    control={<StyledCheckbox {...field} colors={colors} />}
                    label="PSU malfunction"
                  />
                )}
              />
              <Controller
                name="chipTemperature"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{
                      minHeight: "3.5rem",
                      maxWidth: "20rem",
                      minWidth: "20rem",
                      color: colors.text.primary
                    }}
                    control={<StyledCheckbox {...field} colors={colors} />}
                    label="Chip temperature"
                  />
                )}
              />
              <Controller
                name="leakDetection"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{
                      minHeight: "3.5rem",
                      maxWidth: "20rem",
                      minWidth: "20rem",
                      color: colors.text.primary
                    }}
                    control={<StyledCheckbox {...field} colors={colors} />}
                    label="Leak detection"
                  />
                )}
              />
            </Box>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
            marginTop={6}
            gap={2}
          >
            <SecondaryButton colors={colors}
              onClick={() => {
                handleClose();
                reset();
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton colors={colors}
              type="submit"
              disabled={selectedEmails.length === 0}
            >
              Save
            </PrimaryButton>
          </Box>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};

export default NotificationsDialog;
